/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApi } from "@reduxjs/toolkit/query/react";
import baseQueryWithReauth from "./BaseQuery";

export const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["users", "dashboard", "User"],
  endpoints: (builder) => ({
    getChildAccounts: builder.query<any, { page: string; limit: string }>({
      query: ({ page, limit }) => {
        return `/get-users?page=${page}&limit=${limit}`;
      },
      transformResponse: (resp: any) => resp,
      providesTags: ["users"],
    }),
    getChildAccountsWithLimitedData: builder.query({
      query: () => {
        return `/get-users?page=orders`;
      },
      transformResponse: (resp: any) => resp?.childrenAccounts,
      providesTags: ["users"],
    }),
    getUsers: builder.query({
      query: () => {
        return `/fetch-users?page=orders`;
      },
      transformResponse: (resp: any) => resp?.childrenAccounts,
      providesTags: ["users"],
    }),
    getStoreSettings: builder.query({
      query: () => {
        return `/store/default-settings`;
      },
      transformResponse: (resp: any) => resp,
    }),
    getUserStores: builder.query({
      query: () => {
        return `/get-user-stores`;
      },
      transformResponse: (resp: any) => resp?.stores || [],
    }),
    getChildAccount: builder.query({
      query: ({ id }) => {
        return `/get-user-to-edit/${id}`;
      },
      transformResponse: (resp: any) => resp?.user,
      keepUnusedDataFor: 0,
    }),

    createChildAccounts: builder.mutation({
      query: (data) => ({
        url: `/create-user`,
        method: "POST",
        body: { ...data },
      }),
      invalidatesTags: ["users"],
    }),
    postPasswordChangePinCode: builder.mutation({
      query: (data) => ({
        url: `/password/reset-password-pin`,
        method: "POST",
        body: { ...data },
      }),
    }),
    postCreateNewPassword: builder.mutation({
      query: (data) => ({
        url: `/password/reset-password?hash=${data.hash}`,
        method: "POST",
        body: { ...data },
      }),
    }),
    editChildAccounts: builder.mutation({
      query: (data) => ({
        url: `/edit-user/${data?.id}`,
        method: "PUT",
        body: { ...data },
      }),
      invalidatesTags: ["users"],
    }),
    loginUserAccount: builder.mutation({
      query: (data) => {
        return {
          url: `/login-user`,
          method: "POST",
          body: { ...data },
        };
      },
    }),
    restrictAccount: builder.mutation({
      query: (data) => ({
        url: `/restrict-user`,
        method: "PUT",
        body: { id: data.id },
      }),
      invalidatesTags: ["users"],
    }),
    changePassword: builder.mutation({
      query: (data) => ({
        url: `/change-password`,
        method: "PUT",
        body: { ...data },
      }),
      invalidatesTags: ["users"],
    }),
    editTag: builder.mutation({
      query: (data) => ({
        url: `/edit-tag`,
        method: "PUT",
        body: { ...data },
      }),
      invalidatesTags: ["User"],
    }),
    editEmail: builder.mutation({
      query: (data) => ({
        url: `/change-email/${data.id}`,
        method: "PUT",
        body: { ...data },
      }),
      invalidatesTags: ["User"],
    }),
    changePasswordByAdmin: builder.mutation({
      query: (data) => ({
        url: `/change-password/${data.id}`,
        method: "PATCH",
        body: { ...data },
      }),
      invalidatesTags: ["User"],
    }),
    deleteAccount: builder.mutation({
      query: (data) => ({
        url: `/delete-user/${data?.id}`,
        method: "DELETE",
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(usersApi.util.invalidateTags(["users"]));
        }, 500);
      },
    }),
    // send refresh token in header as "x-refresh-token"
    switchStore: builder.mutation({
      query: (storeId: string) => ({
        url: `/switch-store/${storeId}?type=switch`,
        method: "PUT",
        headers: {
          "x-refresh-token": localStorage.getItem("refresh_token") || "",
        },
      }),
      transformResponse: (resp: any) => resp?.data || resp,
    }),
    switchStoreLogin: builder.mutation({
      query: (storeId: string) => ({
        url: `/switch-store-login/${storeId}`,
        method: "PUT",
      }),
      transformResponse: (resp: any) => resp?.data || resp,
    }),
  }),
});

export const {
  useEditTagMutation,
  useEditEmailMutation,
  useGetUserStoresQuery,
  useGetChildAccountQuery,
  useGetChildAccountsQuery,
  useGetStoreSettingsQuery,
  useDeleteAccountMutation,
  useChangePasswordMutation,
  useRestrictAccountMutation,
  useLoginUserAccountMutation,
  useEditChildAccountsMutation,
  useCreateChildAccountsMutation,
  useChangePasswordByAdminMutation,
  usePostCreateNewPasswordMutation,
  usePostPasswordChangePinCodeMutation,
  useGetChildAccountsWithLimitedDataQuery,
  useSwitchStoreMutation,
  useSwitchStoreLoginMutation,
  useGetUsersQuery
} = usersApi;
